<template>
    <section class="section-card additional-guests-card">
        <div class="section-card-header d-flex align-items-center gap-2">
            <h4 class="mb-0">Additional Guests</h4>
        </div>
        <div class="section-card-body">
            <div class="d-flex align-items-center gap-2 existing-guest-picker mb-2">
                <AsyncSelect
                    class="flex-grow-1"
                    placeholder="Select existing guest"
                    v-model="existingGuest"
                    :api-service="fetchContactProfiles"
                    :additional-query="{ type: 'customer' }"
                    :format-label="formatPatientLabel"
                    :reduce="option => option"
                />
                <button type="button" class="btn btn-sm btn-outline-primary text-nowrap" @click="$emit('onAddGuest')">Add Guest</button>
            </div>
            <div v-for="guest in guests" :key="guest.guest_id" class="guest-card mb-2">
                <GuestSummary :guest="guest" removable @onRemove="onRemoveGuest(guest)"/>
            </div>
        </div>
    </section>
</template>

<script setup>
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import GuestSummary from '@/components/molecule/company/hotel/hotel-checkin/GuestSummary.vue';
import { inject, ref, watch } from 'vue';
import handleContact from '@/services/modules/contact';
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper';

const props = defineProps({
    guests: {
        type: Array,
        required: true,
    },
    primaryGuestId: {
        type: Number,
        default: null,
    },
});

const emit = defineEmits(['onAddGuest', 'onLinkGuest', 'onRemoveGuest']);
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();
const showError = inject('showError');

const existingGuest = ref(null);

const isGuestAlreadyAdded = (contactProfileId) => {
    if (String(props.primaryGuestId) === String(contactProfileId)) return true;
    return props.guests.some(guest => String(guest.guest_id) === String(contactProfileId));
};

watch(existingGuest, async (guest) => {
    if (! guest) return;
    if (isGuestAlreadyAdded(guest.id)) {
        showError('This guest has already been added to the reservation.');
        existingGuest.value = null;
        return;
    }
    if (! confirm(`Add ${guest.name} as a guest for this reservation?`)) {
        existingGuest.value = null;
        return;
    }
    emit('onLinkGuest', guest.id);
    existingGuest.value = null;
});

const onRemoveGuest = (guest) => {
    if (! confirm(`Remove ${guest.guest_name} from this reservation?`)) return;
    emit('onRemoveGuest', guest);
};
</script>

<style scoped>
.guest-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.4rem 0.6rem;
    font-size: 0.82rem;
}
.existing-guest-picker {
    width: 100%;
}
.additional-guests-card {
    overflow: visible;
    min-height: 260px;
}
</style>
